import type {
    ClusterDayAssignment,
    ClusterKind,
    ClusterPreviewApply,
    DayPreviewApply,
    DayRateSnapshot,
    PreviewApplyResult,
    ProgramRateSnapshot,
    RateSnapshotData,
    RateSnapshotListItem,
    RoomRateSnapshot,
    SnapshotDayOverride,
} from './types';
import { getDisplayName } from './types';
import type { RoomIsland, ProgramIsland } from './ClusterAssignmentPanel';

export function snapshotItemDisplayName(
    kind: ClusterKind,
    item: RoomRateSnapshot | ProgramRateSnapshot,
): string {
    return kind === 'ROOM'
        ? getDisplayName((item as RoomRateSnapshot).roomType, (item as RoomRateSnapshot).roomTypeName)
        : getDisplayName((item as ProgramRateSnapshot).programType, (item as ProgramRateSnapshot).programTypeName);
}

export function getClusterCurrentDays(
    memberIds: string[],
    kind: ClusterKind,
    targetCurrent: RateSnapshotData | null,
): DayRateSnapshot[] {
    if (!targetCurrent) return [];
    const memberSet = new Set(memberIds);
    const days = kind === 'ROOM'
        ? targetCurrent.rooms.filter(r => memberSet.has(r.reservationRoomId)).flatMap(r => r.dayRates)
        : targetCurrent.programs.filter(p => memberSet.has(p.reservationProgramId)).flatMap(p => p.dayRates);
    return days.slice().sort((a, b) => a.date.localeCompare(b.date));
}

export interface ClusterDayPair {
    date: string;
    current: DayRateSnapshot | null;
    after: DayRateSnapshot | null;
    sourceLabel: string | null;
}

export function buildClusterDayPairs(
    clusterDates: string[],
    currentDays: DayRateSnapshot[],
    days: SnapshotDayOverride[],
    snapshotItems: (RoomRateSnapshot | ProgramRateSnapshot)[],
    kind: ClusterKind,
): ClusterDayPair[] {
    const currentByDate = new Map<string, DayRateSnapshot>();
    for (const d of currentDays) currentByDate.set(d.date, d);
    const overrideByDate = new Map<string, SnapshotDayOverride>();
    for (const d of days) overrideByDate.set(d.targetDate, d);

    return clusterDates.map(date => {
        const current = currentByDate.get(date) ?? null;
        const ov = overrideByDate.get(date);
        if (!ov) {
            return { date, current, after: current, sourceLabel: null };
        }
        const item = snapshotItems[ov.snapshotItemIndex];
        const sourceDay = item?.dayRates[ov.snapshotDayOffset] ?? null;
        const sourceLabel = item
            ? `${snapshotItemDisplayName(kind, item)} (day ${ov.snapshotDayOffset + 1})`
            : null;
        return { date, current, after: sourceDay, sourceLabel };
    });
}

/**
 * Build the apply-preview shape (was a server endpoint) entirely on the client.
 * Inputs are the chosen snapshot, the per-cluster cluster-day assignments, and
 * the targetCurrent rates already loaded for the reservation.
 */
export function buildPreviewApplyResult(
    snapshot: RateSnapshotListItem,
    targetCurrent: RateSnapshotData | null,
    clusterAssignments: ClusterDayAssignment[],
    roomIslands: RoomIsland[],
    programIslands: ProgramIsland[],
): PreviewApplyResult {
    const clusters: ClusterPreviewApply[] = [];

    const warnings: string[] = [];

    for (const a of clusterAssignments) {
        const island = a.clusterKind === 'ROOM'
            ? roomIslands.find(i => i.id === a.clusterId)
            : programIslands.find(i => i.id === a.clusterId);
        if (!island) continue;

        if (a.days.length === 0) {
            warnings.push(`${island.displayName}: no day overrides configured — cluster left untouched.`);
        }

        const currentDays = getClusterCurrentDays(a.memberIds, a.clusterKind, targetCurrent);
        const currentByDate = new Map<string, number>();
        for (const d of currentDays) currentByDate.set(d.date, d.grossAmount);

        const sourceItems = a.clusterKind === 'ROOM'
            ? snapshot.data.rooms
            : snapshot.data.programs;
        const overrideByDate = new Map<string, SnapshotDayOverride>();
        for (const o of a.days) overrideByDate.set(o.targetDate, o);

        let exhaustedCount = 0;
        const days: DayPreviewApply[] = currentDays.map(cd => {
            const date = cd.date;
            const currentGross = currentByDate.get(date) ?? 0;
            const ov = overrideByDate.get(date);
            if (!ov) return { date, currentGross, snapshotGross: null, sourceLabel: null };
            const item = sourceItems[ov.snapshotItemIndex];
            const sourceDay = item?.dayRates[ov.snapshotDayOffset];
            if (!item || !sourceDay) {
                exhaustedCount++;
                return { date, currentGross, snapshotGross: null, sourceLabel: null };
            }
            return {
                date,
                currentGross,
                snapshotGross: sourceDay.grossAmount,
                sourceLabel: `${snapshotItemDisplayName(a.clusterKind, item)} (day ${ov.snapshotDayOffset + 1})`,
            };
        });

        if (exhaustedCount > 0) {
            warnings.push(`${island.displayName}: snapshot data exhausted — ${exhaustedCount} day(s) left uncovered.`);
        }

        const uncoveredCount = a.days.length > 0
            ? days.filter(d => d.snapshotGross === null).length
            : 0;
        if (uncoveredCount > 0 && uncoveredCount !== exhaustedCount) {
            warnings.push(`${island.displayName}: ${uncoveredCount} cluster day(s) have no snapshot coverage.`);
        }

        clusters.push({
            clusterId: a.clusterId,
            clusterKind: a.clusterKind,
            displayName: island.displayName,
            days,
        });
    }

    return { clusters, warnings };
}

